import React, { useCallback, useEffect, useState } from "react";
import { Link, useOutletContext, useSearchParams } from "react-router-dom";
import LeftAngle from "../../assets/image/left-angle.svg";
import RightAngle from "../../assets/image/right-angle.svg";
import DownloadIcon from "../../assets/image/download.svg";
import { getApiErrorMessage, handleApiCall } from "../../apis";
import DateRangeFilter from "../../Components/DateRangeFilter/DateRangeFilter";
import DateRangeAppliedLabel from "../../Components/DateRangeFilter/DateRangeAppliedLabel";
import { endpoint } from "../../utils/endpoints";
import Toastify, { notifyError, notifySuccess } from "../../utils/tostfy";
import {
  appendDateRangeParams,
  DATE_PARAM_KEYS,
  parseApiDate,
  toApiDate,
  validateDateRange,
} from "../../utils/dateRange";
import CsvStatusBadge from "../../Components/CsvStatusBadge/CsvStatusBadge";
import { useAdminCsvReady } from "../../hooks/useAdminCsvReady";
import {
  isDriverLedgerCsvType,
  patchSalaryCsvLogRow,
  resolveCsvStatusLabel,
} from "../../utils/adminCsvSocket";
import { formatDateTime } from "../../utils/formatDateTime";
import { useBackToList } from "../../hooks/useBackToList";
import { formatBalanceRupees, formatLedgerAmount } from "../../utils/formatAmount";
import PermissionGate from "../../Components/PermissionGate/PermissionGate";
import { MODULES } from "../../utils/permissions";
import PageNumberInput from "../../Components/Pagination/PageNumberInput";

const C = {
  border: "#E5E7EB",
  text: "#111827",
  muted: "#6B7280",
  primary: "#000053",
  tableHead: "#000053",
};

/** Driver ledger / export API type values (e.g. type=Trip Advance) */
const DRIVER_LEDGER_TYPE_OPTIONS = [
  { label: "All Types", value: "" },
  { label: "Normal Advance", value: "Normal Advance" },
  { label: "Salary Amount", value: "Salary Amount" },
  { label: "Award Amount", value: "Award Amount" },
  { label: "Trip Advance", value: "Trip Advance" },
];

const appendDriverLedgerType = (params, typeValue) => {
  if (typeValue) params.set("type", typeValue);
};

const ViewTabButton = ({ active, label, onClick }) => (
  <button
    type="button"
    onClick={onClick}
    className={`md:h-10 h-9 lg:min-w-[7.5rem] rounded-md border px-6 text-sm font-UbuntuMedium transition-colors max-[991px]:!rounded-full ${
      active
        ? "border-[#000053] bg-[#000053] text-white"
        : "border-[#000053] bg-white text-[#000053] hover:bg-[#000053]/5"
    }`}
  >
    {label}
  </button>
);

function formatLedgerCell(value) {
  return formatLedgerAmount(value);
}

function formatLogFilters(filter) {
  if (filter == null || filter === "") return "—";
  if (typeof filter === "string") return filter.trim() || "—";
  if (typeof filter !== "object") return "—";

  const parts = [];
  const typeVal =
    filter.type ?? filter.transaction_type ?? filter.transition_type ?? filter.verification_type;
  if (typeVal) parts.push(`Transition Type: ${typeVal}`);
  if (filter.from_date && filter.to_date) {
    parts.push(`From ${filter.from_date} to ${filter.to_date}`);
  } else if (filter.from_date) {
    parts.push(`From ${filter.from_date}`);
  } else if (filter.to_date) {
    parts.push(`To ${filter.to_date}`);
  }
  return parts.length ? parts.join(", ") : "—";
}

function truncateWords(value, maxWords = 50, maxChars = 220) {
  const text = value == null ? "" : String(value).trim();
  if (!text) return "—";
  const words = text.split(/\s+/);
  const wordLimited =
    words.length <= maxWords ? text : words.slice(0, maxWords).join(" ");
  if (wordLimited.length <= maxChars) return wordLimited;
  return `${wordLimited.slice(0, maxChars).trimEnd()}...`;
}

function isDescriptionTruncated(value, maxWords = 50, maxChars = 220) {
  const text = value == null ? "" : String(value).trim();
  if (!text || text === "—") return false;
  const words = text.split(/\s+/);
  return words.length > maxWords || text.length > maxChars;
}

const mapLedgerRow = (r) => ({
  id: r?._id ?? "",
  dateTime: formatDateTime(r?.created_at),
  verificationType: r?.type ?? "—",
  tripId:
    r?.trip_ref_id != null && r.trip_ref_id !== ""
      ? String(r.trip_ref_id).replace(/^#/, "")
      : "—",
  credit: formatLedgerCell(r?.credit),
  debit: formatLedgerCell(r?.debit),
  runningBalance: formatLedgerCell(r?.balance),
  description:
    r?.description != null && String(r.description).trim() !== ""
      ? String(r.description).trim()
      : "—",
});

const mapDriverLedgerCsvLogRow = (item, index) => {
  const link = item?.csv_link || item?.file_url || item?.download_url || "";
  const fileName =
    item?.file_name ||
    item?.filename ||
    (link ? decodeURIComponent(String(link).split("/").pop()?.split("?")[0] || "") : "") ||
    "—";

  return {
    id: item?._id || item?.id || item?.csv_log_id || `log-${index}`,
    fileName,
    filters: formatLogFilters(item?.filter ?? item?.filters),
    createdAt: formatDateTime(item?.createdAt || item?.created_at),
    status: resolveCsvStatusLabel(item?.status, link),
    downloadUrl: link,
  };
};

const extractCsvLogList = (data) => {
  if (Array.isArray(data)) return data;
  if (Array.isArray(data?.csv_log_data)) return data.csv_log_data;
  if (Array.isArray(data?.driver_ledger_csv_log)) return data.driver_ledger_csv_log;
  if (Array.isArray(data?.payment_csv_log)) return data.payment_csv_log;
  return [];
};

const SalaryAwardsDetail = () => {
  const { setSubheading } = useOutletContext();
  const [searchParams] = useSearchParams();
  const driverId = searchParams.get("driver_id") || "";
  const backToList = useBackToList("/salary-awards");

  const [activeView, setActiveView] = useState("listing");
  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");
  const [pickerRange, setPickerRange] = useState([null, null]);
  const [transactionType, setTransactionType] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(10);
  const [rows, setRows] = useState([]);
  const [listLoading, setListLoading] = useState(true);
  const [driverName, setDriverName] = useState("—");
  const [currentBalance, setCurrentBalance] = useState(null);
  const [totalSalary, setTotalSalary] = useState(null);
  const [totalAward, setTotalAward] = useState(null);
  const [totalRecords, setTotalRecords] = useState(0);
  const [totalPages, setTotalPages] = useState(1);
  const [exportLoading, setExportLoading] = useState(false);

  const [logRows, setLogRows] = useState([]);
  const [logLoading, setLogLoading] = useState(false);

  const dateRangeCheck = validateDateRange(fromDate, toDate);
  const dateValidationError = dateRangeCheck.valid ? "" : dateRangeCheck.message;

  useEffect(() => {
    setSubheading("View Salary & Awards Details");
    return () => {
      setSubheading("");
    };
  }, [setSubheading]);

  useEffect(() => {
    setPickerRange([parseApiDate(fromDate), parseApiDate(toDate)]);
  }, [fromDate, toDate]);

  useEffect(() => {
    setCurrentPage(1);
  }, [driverId, fromDate, toDate, transactionType]);

  const fetchDriverLedger = useCallback(async () => {
    if (!driverId) {
      setRows([]);
      setListLoading(false);
      setTotalRecords(0);
      setTotalPages(1);
      return;
    }
    if (dateValidationError) {
      setRows([]);
      setListLoading(false);
      setTotalRecords(0);
      setTotalPages(1);
      return;
    }

    setListLoading(true);
    try {
      const params = new URLSearchParams({
        page: String(currentPage),
        limit: String(itemsPerPage),
        driver_id: driverId,
      });
      appendDateRangeParams(
        params,
        { start: fromDate, end: toDate },
        DATE_PARAM_KEYS.fromTo
      );
      appendDriverLedgerType(params, transactionType);

      const response = await handleApiCall(
        "get",
        `${endpoint.listOfDriverLedger}?${params.toString()}`,
        {}
      );

      if (response?.data?.code === 200) {
        const d = response?.data?.data || {};
        const list = Array.isArray(d?.driver_ledger_data) ? d.driver_ledger_data : [];
        setRows(list.map(mapLedgerRow));
        setDriverName(d?.driver_full_name ?? "—");
        setCurrentBalance(d?.current_balance ?? null);
        setTotalSalary(d?.total_salary ?? null);
        setTotalAward(d?.total_award ?? null);
        setTotalRecords(d?.total ?? list.length);
        setTotalPages(Math.max(1, d?.total_pages ?? 1));
      } else {
        setRows([]);
        setTotalRecords(0);
        setTotalPages(1);
        notifyError(getApiErrorMessage(response, "Failed to load driver ledger"));
      }
    } catch {
      setRows([]);
      setTotalRecords(0);
      setTotalPages(1);
      notifyError("Failed to load driver ledger");
    } finally {
      setListLoading(false);
    }
  }, [
    driverId,
    currentPage,
    itemsPerPage,
    fromDate,
    toDate,
    transactionType,
    dateValidationError,
  ]);

  const fetchDriverLedgerCsvLog = useCallback(async () => {
    if (!driverId) {
      setLogRows([]);
      return;
    }
    setLogLoading(true);
    try {
      const params = new URLSearchParams({ driver_id: driverId });
      const response = await handleApiCall(
        "get",
        `${endpoint.getDriverLedgerCsvLog}?${params.toString()}`,
        {}
      );

      if (response?.data?.code === 200) {
        const d = response?.data?.data || {};
        const list = extractCsvLogList(d);
        setLogRows(list.map(mapDriverLedgerCsvLogRow));
      } else {
        setLogRows([]);
        notifyError(getApiErrorMessage(response, "Failed to load CSV log"));
      }
    } catch {
      setLogRows([]);
      notifyError("Failed to load CSV log");
    } finally {
      setLogLoading(false);
    }
  }, [driverId]);

  useEffect(() => {
    if (activeView !== "listing") return;
    fetchDriverLedger();
  }, [activeView, fetchDriverLedger]);

  useEffect(() => {
    if (activeView !== "log") return;
    fetchDriverLedgerCsvLog();
  }, [activeView, fetchDriverLedgerCsvLog]);

  const handleCsvReady = useCallback(
    (payload) => {
      if (!isDriverLedgerCsvType(payload?.type)) return;

      const logId = payload?.csv_log_id;
      setLogRows((prev) => {
        const idx = logId ? prev.findIndex((row) => row.id === logId) : -1;
        if (idx >= 0) {
          const next = [...prev];
          next[idx] = patchSalaryCsvLogRow(prev[idx], payload);
          return next;
        }
        return prev;
      });
      fetchDriverLedgerCsvLog();
    },
    [fetchDriverLedgerCsvLog]
  );

  useAdminCsvReady(handleCsvReady);

  const hasListingData = !listLoading && totalRecords > 0;

  const handleApplyDates = () => {
    const [start, end] = pickerRange;
    if (!start || !end) {
      notifyError("Please select start and end dates");
      return false;
    }
    setFromDate(toApiDate(start));
    setToDate(toApiDate(end));
    setCurrentPage(1);
    return true;
  };

  const handleClearDates = () => {
    setPickerRange([null, null]);
    setFromDate("");
    setToDate("");
    setCurrentPage(1);
  };

  const clearListingFilters = () => {
    handleClearDates();
    setTransactionType("");
    setCurrentPage(1);
  };

  const hasDateRangeSelected = Boolean(fromDate && toDate);
  const hasAnyListingFilter = Boolean(
    hasDateRangeSelected ||
      transactionType ||
      pickerRange[0] ||
      pickerRange[1]
  );

  const handleExportCsv = async () => {
    if (!driverId) {
      notifyError("Driver not found");
      return;
    }
    if (!hasListingData) {
      notifyError("No ledger data to export");
      return;
    }

    setExportLoading(true);
    try {
      const params = new URLSearchParams({ driver_id: driverId });
      appendDateRangeParams(
        params,
        { start: fromDate, end: toDate },
        DATE_PARAM_KEYS.fromTo
      );
      appendDriverLedgerType(params, transactionType);

      const response = await handleApiCall(
        "get",
        `${endpoint.getDriverLedgerCSV}?${params.toString()}`,
        {}
      );

      if (response?.data?.code === 200) {
        await fetchDriverLedgerCsvLog();
        return;
      }

      notifyError(getApiErrorMessage(response, "Failed to export CSV"));
    } catch {
      notifyError("Failed to export CSV");
    } finally {
      setExportLoading(false);
    }
  };

  const handleDownloadLogFile = (url) => {
    if (!url) {
      notifyError("Download link not available");
      return;
    }
    window.open(url, "_blank", "noopener,noreferrer");
  };

  const listingPage = Math.min(currentPage, totalPages);
  const listingFirstIndex = (listingPage - 1) * itemsPerPage;

  return (
    <>
      <Toastify />
      <div className="tab-pane-content md:mt-7 mt-3 font-UbuntuRegular">
        <Link to={backToList} className="btn btn-primary ml-auto !mb-4 md:!mb-6">
          Back
        </Link>

        {!driverId && (
          <p className="text-sm mb-4" style={{ color: C.muted }}>
            No driver selected. Open a ledger from the Salary Awards list.
          </p>
        )}

        <div
          className="rounded-md border overflow-hidden bg-white !mb-4 !md:mb-5"
          style={{ borderColor: C.border }}
        >
          <div className="md:px-6 md:py-4 px-3 py-3 flex flex-wrap gap-x-8 gap-y-2">
            <p className="md:text-[18px] leading-none font-UbuntuMedium" style={{ color: C.text }}>
              Driver:{" "}
              <span className="text-gray-600 font-UbuntuRegular">{driverName}</span>
            </p>
            <p className="md:text-[18px] leading-none font-UbuntuMedium" style={{ color: C.text }}>
              Current Balance:{" "}
              <span className="text-gray-600 font-UbuntuRegular">
                {formatBalanceRupees(currentBalance)}
              </span>
            </p>
          </div>
          <div
            className="border-t md:px-6 md:py-3 px-3 py-2 flex flex-wrap gap-8 text-sm"
            style={{ borderColor: C.border }}
          >
            <p style={{ color: C.muted }}>
              Salary{" "}
              <span className="ml-2 font-UbuntuMedium text-gray-700">
                {formatBalanceRupees(totalSalary)}
              </span>
            </p>
            <p style={{ color: C.muted }}>
              Award{" "}
              <span className="ml-2 font-UbuntuMedium text-gray-700">
                {formatBalanceRupees(totalAward)}
              </span>
            </p>
          </div>
        </div>

        <div className="md:!mb-4 !mb-3 flex flex-wrap md:gap-2 gap-1">
          <ViewTabButton
            active={activeView === "listing"}
            label="Listing"
            onClick={() => setActiveView("listing")}
          />
          <ViewTabButton
            active={activeView === "log"}
            label="Log CSV"
            onClick={() => setActiveView("log")}
          />
        </div>

        {activeView === "listing" && (
          <>
            <div className="mb-4 flex flex-wrap items-center md:gap-x-4 md:gap-y-3 gap-x-1 gap-y-1 relative max-[767px]:items-end">
              <div className="inline-flex h-11 flex-nowrap items-center gap-2.5 max-[767px]:absolute max-[767px]:-top-14 max-[767px]:mt-1 max-[767px]:right-0 max-[767px]:[&_.relative_button]:h-[36px] max-[767px]:[&_.relative_button]:w-[36px]  max-[767px]:[&_.relative_button_img]:h-4 max-[767px]:[&_.relative_.absolute]:right-1 max-[767px]:[&_.relative_.absolute]:!left-auto">
                <DateRangeFilter
                  variant="popover"
                  bare
                  toolbarIcon
                  range={pickerRange}
                  onRangeChange={setPickerRange}
                  onApply={handleApplyDates}
                  applyDisabled={!pickerRange[0] || !pickerRange[1]}
                  onClear={handleClearDates}
                  maxDate={new Date()}
                />
                {hasDateRangeSelected && (
                  <DateRangeAppliedLabel
                    from={fromDate}
                    to={toDate}
                    prefix="Date range: "
                    className="shrink-0 whitespace-nowrap"
                  />
                )}
              </div>

              <div className="inline-flex md:h-11 shrink-0 items-center md:gap-2 gap-1 max-[767px]:flex-col max-[767px]:items-start">
                <label
                  className="mb-0 whitespace-nowrap text-xs font-UbuntuMedium"
                  style={{ color: C.muted }}
                >
                  Transition Type
                </label>
                <select
                  className="form-control h-10 min-w-[10rem] rounded-lg border text-sm"
                  style={{ borderColor: C.border }}
                  value={transactionType}
                  onChange={(e) => {
                    setTransactionType(e.target.value);
                    setCurrentPage(1);
                  }}
                >
                  {DRIVER_LEDGER_TYPE_OPTIONS.map((opt) => (
                    <option key={opt.value || "all"} value={opt.value}>
                      {opt.label}
                    </option>
                  ))}
                </select>
              </div>

              {hasAnyListingFilter && (
                <button
                  type="button"
                  onClick={clearListingFilters}
                  className="md:h-10 shrink-0 whitespace-nowrap rounded-lg border border-gray-300 bg-white md:px-4 !px-2.5 h-9 md:text-sm text-xs font-UbuntuMedium text-gray-800 md:shadow-sm hover:border-gray-400 hover:bg-gray-50 max-[767px]:h-[40px] max-[767px]:rounded-full"
                >
                  Clear Filters
                </button>
              )}

              <PermissionGate module={MODULES.SALARY_AWARDS} action="export">
                <button
                  type="button"
                  className="ml-auto btn btn-primary max-[767px]:mt-4 max-[767px]:!px-3 max-[767px]:h-[40px] max-[767px]:text-xs"
                  style={{ background: C.primary }}
                  onClick={handleExportCsv}
                  disabled={exportLoading || !hasListingData || !driverId}
                  title={
                    !hasListingData ? "Export is available when listing has data" : undefined
                  }
                >
                  {exportLoading ? "Exporting.." : "Export"}
                </button>
              </PermissionGate>
            </div>

            {dateValidationError ? (
              <p className="mb-3 text-sm text-red-500">{dateValidationError}</p>
            ) : null}

            <div className="common-table-wrapper m-0 rounded-xl border shadow-sm" style={{ borderColor: C.border }}>
              <div className="m-0 w-full overflow-x-auto">
                <table className="mb-0 w-full min-w-[1050px] bg-white">
                  <thead>
                    <tr>
                      {[
                        "S.No.",
                        "Date & Time",
                        "Verification Type",
                        "Trip ID",
                        "Credit +",
                        "Debit -",
                        "Running Balance",
                        "Description",
                      ].map((title) => (
                        <th
                          key={title}
                          className={`whitespace-nowrap border-0 px-3 py-3.5 text-[13px] font-UbuntuMedium text-white ${
                            title === "Description" ? "text-center" : ""
                          }`}
                        >
                          {title}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {listLoading ? (
                      <tr>
                        <td colSpan={8} className="text-center py-10" style={{ color: C.muted }}>
                          Loading...
                        </td>
                      </tr>
                    ) : rows.length === 0 ? (
                      <tr>
                        <td colSpan={8} className="text-center py-10" style={{ color: C.muted }}>
                          No transactions found.
                        </td>
                      </tr>
                    ) : (
                      rows.map((row, i) => (
                        <tr key={row.id || i} className={i % 2 ? "bg-[#FAFAFA]" : "bg-white"}>
                          <td className="px-3 md:py-4 py-2 text-center">
                            {String(listingFirstIndex + i + 1).padStart(2, "0")}
                          </td>
                          <td className="px-3 md:py-4 py-2 whitespace-nowrap text-center">{row.dateTime}</td>
                          <td className="px-3 md:py-4 py-2 text-center">{row.verificationType}</td>
                          <td className="px-3 md:py-4 py-2 text-center">{row.tripId}</td>
                          <td className="px-3 md:py-4 py-2 text-center">{row.credit}</td>
                          <td className="px-3 md:py-4 py-2 text-center">{row.debit}</td>
                          <td className="px-3 md:py-4 py-2 text-center">{row.runningBalance}</td>
                          <td
                            className="max-w-[220px] px-3 md:py-4 py-2 text-center align-top"
                            title={
                              isDescriptionTruncated(row.description)
                                ? row.description
                                : undefined
                            }
                            style={{
                              whiteSpace: "normal",
                              wordBreak: "break-word",
                              overflowWrap: "anywhere",
                              lineHeight: 1.4,
                            }}
                          >
                            {truncateWords(row.description, 50)}
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
            </div>

            <div className="lg:!mt-5 !mt-2 md:!mt-4 flex items-center justify-end gap-2 md:gap-3 text-lg">
              <span className="text-xs md:text-sm text-gray-500 md:ml-auto max-[767px]:mr-auto">
                {totalRecords} record{totalRecords === 1 ? "" : "s"} found
              </span>
              <button
                type="button"
                onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
                disabled={listingPage === 1 || listLoading}
                className="!bg-primary text-white md:w-[35px] md:h-[35px] w-[25px] h-[25px] rounded-full disabled:opacity-50 inline-flex justify-center items-center "
              >
                <img src={LeftAngle} className="invert grayscale brightness-100" alt="" />
              </button>
              <PageNumberInput
                page={listingPage}
                totalPages={totalPages}
                onPageChange={setCurrentPage}
                className="h-[35px] w-10 md:w-12 rounded border border-gray-300 text-center text-sm font-UbuntuMedium shadow-sm outline-none disabled:cursor-not-allowed disabled:bg-gray-100 disabled:opacity-70"
              />
              <span className="text-sm text-gray-600 text-nowrap">of {totalPages}</span>
              <button
                type="button"
                onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
                disabled={listingPage === totalPages || listLoading}
                className="!bg-primary text-white md:w-[35px] md:h-[35px] w-[25px] h-[25px] rounded-full disabled:opacity-50 inline-flex justify-center items-center "
              >
                <img src={RightAngle} className="invert grayscale brightness-100" alt="" />
              </button>
            </div>
          </>
        )}

        {activeView === "log" && (
          <>
            <div className="common-table-wrapper m-0 rounded-xl border shadow-sm" style={{ borderColor: C.border }}>
              <div className="m-0 w-full overflow-x-auto">
                <table className="mb-0 w-full min-w-[900px] bg-white">
                  <thead>
                    <tr>
                      {["S.No.", "File Name", "Filters", "Created Date", "Status", "Action"].map((title) => (
                        <th
                          key={title}
                          className="whitespace-nowrap border-0 px-3 py-3.5 text-center text-[13px] font-UbuntuMedium text-white"
                        >
                          {title}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {logLoading ? (
                      <tr>
                        <td colSpan={6} className="py-10 text-center" style={{ color: C.muted }}>
                          Loading...
                        </td>
                      </tr>
                    ) : logRows.length === 0 ? (
                      <tr>
                        <td colSpan={6} className="py-10 text-center" style={{ color: C.muted }}>
                          No CSV exports found.
                        </td>
                      </tr>
                    ) : (
                      logRows.map((row, i) => (
                        <tr key={row.id} className={i % 2 ? "bg-[#FAFAFA]" : "bg-white"}>
                          <td className="px-3 md:py-4 py-2 text-center">
                            {String(i + 1).padStart(2, "0")}
                          </td>
                          <td
                            className="max-w-[240px] truncate px-3 md:py-4 py-2 text-center"
                            title={row.fileName}
                          >
                            {row.fileName}
                          </td>
                          <td className="px-3 md:py-4 py-2 text-center">{row.filters}</td>
                          <td className="whitespace-nowrap px-3 md:py-4 py-2 text-center">
                            {row.createdAt}
                          </td>
                          <td className="px-3 md:py-4 py-2 text-center">
                            <CsvStatusBadge status={row.status} />
                          </td>
                          <td className="px-3 md:py-4 py-2 text-center">
                            {row.status === "Complete" && row.downloadUrl ? (
                              <button
                                type="button"
                                onClick={() => handleDownloadLogFile(row.downloadUrl)}
                                className="inline-flex md:h-9 md:w-9 h-7 w-7 items-center justify-center rounded-full border-0 !bg-primary"
                                aria-label={`Download ${row.fileName}`}
                              >
                                <img src={DownloadIcon} alt="" className="md:h-3.5 md:w-3.5 h-2.5 w-2.5" />
                              </button>
                            ) : (
                              "—"
                            )}
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
            </div>

            <div className="mt-2 flex justify-end text-lg">
              <span className="text-xs md:text-base text-gray-500 ml-auto">
                {logRows.length} record{logRows.length === 1 ? "" : "s"} found
              </span>
            </div>
          </>
        )}
      </div>
    </>
  );
};

export default SalaryAwardsDetail;
